// Tipos del dominio "Cobranza" (Operations). Contrato del admin-api:
//   GET /api/admin/operations/collections
// y del cruce con el CRM (tickets de impago/disputa):
//   GET /tickets/payment-issues
//
// El backend está en desarrollo en paralelo; estos tipos fijan el contrato que
// consume la vista.

import { LeadStatus } from "./onboarding";

// Estado de un mes concreto de la póliza (una columna del calendario de 4 meses).
export const MONTH_STATUSES = [
  "PAID",
  "SENT",
  "UNPAID",
  "DISPUTED",
  // Q2 auditoría 2026-08-09: dinero devuelto — ni cobro perfecto ni impago
  // clásico. Antes caía en NONE y la cobranza salía inflada.
  "REFUNDED",
  "VOID",
  "NONE",
] as const;
export type MonthStatus = (typeof MONTH_STATUSES)[number];

// Clasificación agregada de la póliza según su historial de cobro.
// Orden = precedencia del read model backend (primera que aplica gana):
//   claim → canceled → in_progress → disputed → unpaid → doubtful → perfect.
export const COLLECTION_CLASSIFICATIONS = [
  "perfect",
  "doubtful",
  "disputed",
  "unpaid",
  "in_progress",
  "canceled",
  "claim",
] as const;
export type CollectionClassification =
  (typeof COLLECTION_CLASSIFICATIONS)[number];

// De dónde sale el medor asignado: regla general o excepción manual.
export type MedorSource = "rule" | "exception";

export type CollectionMonth = {
  month: string; // 'YYYY-MM'
  status: MonthStatus;
  paymentDate: string | null;
};

export type CollectionRow = {
  leadUlid: string;
  policyNumber: string;
  fullName: string;
  market: string | null;
  brokerId: string | null;
  brokerName: string | null;
  medor: string | null;
  medorSource: MedorSource;
  leadStatus: LeadStatus;
  paymentMethod: string | null;
  frequency: string | null;
  subscriptionStatus: string | null;
  months: CollectionMonth[];
  classification: CollectionClassification;
  unpaidAtCut: boolean;
};

// Contadores globales por clasificación sobre TODO el universo filtrado
// (server-side, no solo la página). unpaidAtCut cuenta las pólizas no pagadas
// al corte del día 15 dentro del mismo universo.
export type CollectionCounts = {
  perfect: number;
  doubtful: number;
  disputed: number;
  unpaid: number;
  inProgress: number;
  canceled: number;
  claim: number;
  unpaidAtCut: number;
};

export type CollectionsResponse = {
  items: CollectionRow[];
  total: number;
  page: number;
  pageSize: number;
  months: string[]; // 4 strings 'YYYY-MM' (orden del calendario)
  // Contadores globales del backend. Opcional por compatibilidad: si el backend
  // aún no lo envía, la vista cae al recuento de la página visible.
  counts?: CollectionCounts;
};

// ---- Cruce CRM: tickets de impago / disputa ------------------------------
// El contrato del CRM vive en api/crmTypes.ts (junto al resto del dominio de
// tickets); lo re-exportamos aquí para que la vista importe todo desde un sitio.
export type {
  PaymentIssueTicket,
  PaymentIssuesResponse,
} from "../api/crmTypes";

// ---- Etiquetas / estilos (es-ES) -----------------------------------------

export const MONTH_STATUS_LABEL: Record<MonthStatus, string> = {
  PAID: "Pagado",
  SENT: "Enviado",
  UNPAID: "Impagado",
  DISPUTED: "Disputado",
  REFUNDED: "Devuelto",
  VOID: "Anulado",
  NONE: "—",
};

export const MONTH_STATUS_STYLES: Record<
  MonthStatus,
  { bg: string; fg: string }
> = {
  PAID: { bg: "#d4edda", fg: "#155724" }, // verde
  SENT: { bg: "#fff3cd", fg: "#856404" }, // ámbar
  UNPAID: { bg: "#f8d7da", fg: "#b32424" }, // rojo
  DISPUTED: { bg: "#e6dcf5", fg: "#5b3a9c" }, // morado
  REFUNDED: { bg: "#ffe5d0", fg: "#a4510a" }, // naranja: dinero que volvió
  VOID: { bg: "#e9ecef", fg: "#6c757d" }, // gris
  NONE: { bg: "transparent", fg: "#adb5bd" }, // vacío
};

export const CLASSIFICATION_LABEL: Record<CollectionClassification, string> = {
  perfect: "Perfecta",
  doubtful: "Dudosa",
  disputed: "Disputada",
  unpaid: "Impagada",
  in_progress: "En trámite",
  canceled: "Cancelada",
  claim: "Siniestro",
};

export const CLASSIFICATION_STYLES: Record<
  CollectionClassification,
  { bg: string; fg: string }
> = {
  perfect: { bg: "#d4edda", fg: "#155724" }, // verde
  doubtful: { bg: "#fff3cd", fg: "#856404" }, // ámbar
  disputed: { bg: "#e6dcf5", fg: "#5b3a9c" }, // morado
  unpaid: { bg: "#f8d7da", fg: "#b32424" }, // rojo
  in_progress: { bg: "#d6e9fb", fg: "#1c5a94" }, // azul claro
  canceled: { bg: "#e9ecef", fg: "#6c757d" }, // gris
  claim: { bg: "#fbe4c9", fg: "#8a4b00" }, // naranja/ámbar oscuro
};
